import { useEffect, useState, useCallback, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { AddClientDialog } from "@/components/AddClientDialog";
import { EditClientDialog } from "@/components/EditClientDialog";
import { AddBillDialog } from "@/components/AddBillDialog";
import { EditBillDialog } from "@/components/EditBillDialog";
import { ChatWindow } from "@/components/ChatWindow";
import { TallySyncPanel } from "@/components/TallySyncPanel";
import { GroupStatsPanel } from "@/components/GroupStatsPanel";
import api, { formatApiError } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { generateInvoicePDF } from "@/lib/pdf";
import { sendInvoiceViaWhatsApp, sendInvoiceViaEmail, copyInvoiceMessage } from "@/lib/sendInvoice";
import { useBusinessInfo } from "@/lib/businessInfo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus,
  Users,
  FileText,
  IndianRupee,
  MoreHorizontal,
  Download,
  Search,
  Pencil,
  MessageSquare,
  Mail,
  MessageCircle,
  Copy,
} from "lucide-react";

const StatCard = ({ label, value, sublabel, icon: Icon, testId }) => (
  <Card className="border-slate-200 shadow-none rounded-md p-6" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="label-tag">{label}</p>
        <p className="mt-3 font-mono text-3xl font-medium text-slate-900 tracking-tight">
          {value}
        </p>
        {sublabel && <p className="text-xs text-slate-500 mt-2">{sublabel}</p>}
      </div>
      {Icon && (
        <div className="w-9 h-9 flex items-center justify-center border border-slate-200 bg-slate-50 rounded-md">
          <Icon className="w-4 h-4 text-slate-700" />
        </div>
      )}
    </div>
  </Card>
);

export default function AdminDashboard() {
  const businessInfo = useBusinessInfo();
  const { user } = useAuth();
  // Office/Invoice role: restricted view — hides billed/paid totals across the workspace.
  const RESTRICTED_EMAILS = ["office@billing.com", "invoice@billing.com"];
  const isOfficeView = RESTRICTED_EMAILS.includes((user?.email || "").toLowerCase());
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddClient, setOpenAddClient] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [openAddBill, setOpenAddBill] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [presetClientId, setPresetClientId] = useState(null);
  const [searchClient, setSearchClient] = useState("");
  const [searchBill, setSearchBill] = useState("");
  const [chatClientId, setChatClientId] = useState(null);
  const [unreadByClient, setUnreadByClient] = useState({});
  const [searchThread, setSearchThread] = useState("");

  const totalUnread = useMemo(
    () => Object.values(unreadByClient).reduce((a, b) => a + b, 0),
    [unreadByClient]
  );

  const loadAll = useCallback(async () => {
    try {
      const [s, c, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/clients"),
        api.get("/admin/bills"),
      ]);
      setStats(s.data);
      setClients(c.data);
      setBills(b.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Poll admin unread counts every 6s
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (!cancelled) setUnreadByClient(data.by_client || {});
      } catch {
        // Polling error is transient; the next tick recovers. Intentionally silent.
      }
    };
    tick();
    const id = setInterval(tick, 6000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const handleToggleStatus = async (bill) => {
    const newStatus = bill.status === "paid" ? "unpaid" : "paid";
    try {
      await api.patch(`/admin/bills/${bill.id}`, { status: newStatus });
      toast.success(`Marked ${bill.invoice_no} as ${newStatus}`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteBill = async (bill) => {
    if (!window.confirm(`Delete invoice ${bill.invoice_no}?`)) return;
    try {
      await api.delete(`/admin/bills/${bill.id}`);
      toast.success("Invoice deleted");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteClient = async (c) => {
    if (!window.confirm(`Delete client ${c.name}? This removes all their invoices.`))
      return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Client deleted");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filteredClients = clients.filter((c) =>
    (c.name + c.email + (c.phone || "")).toLowerCase().includes(searchClient.toLowerCase())
  );
  const filteredBills = bills.filter((b) =>
    (b.invoice_no + b.client_name + b.client_email)
      .toLowerCase()
      .includes(searchBill.toLowerCase())
  );

  return (
    <AppShell title="Admin Dashboard" subtitle="Manage clients, services, and payments.">
      {/* Stats */}
      <div
        className={`grid grid-cols-1 gap-4 mb-8 ${
          isOfficeView ? "sm:grid-cols-1 max-w-xs" : "sm:grid-cols-2 lg:grid-cols-4"
        }`}
      >
        <StatCard
          label="Clients"
          value={stats?.clients_count ?? "—"}
          icon={Users}
          testId="stat-clients"
        />
        {!isOfficeView && (
          <StatCard
            label="Total Billed"
            value={stats ? formatINR(stats.total_billed) : "—"}
            sublabel={`${stats?.bills_count ?? 0} invoices`}
            icon={FileText}
            testId="stat-total-billed"
          />
        )}
        {!isOfficeView && (
          <StatCard
            label="Total Paid"
            value={stats ? formatINR(stats.total_paid) : "—"}
            icon={IndianRupee}
            testId="stat-total-paid"
          />
        )}
        {!isOfficeView && (
          <StatCard
            label="Outstanding"
            value={stats ? formatINR(stats.total_unpaid) : "—"}
            sublabel="Unpaid invoices"
            icon={IndianRupee}
            testId="stat-total-unpaid"
          />
        )}
      </div>

      {!isOfficeView && <TallySyncPanel />}
      {!isOfficeView && <GroupStatsPanel />}

      <Tabs defaultValue="bills" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-md p-1 h-11">
          <TabsTrigger
            value="bills"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm px-4"
            data-testid="tab-bills"
          >
            Invoices
          </TabsTrigger>
          <TabsTrigger
            value="clients"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm px-4"
            data-testid="tab-clients"
          >
            Clients
          </TabsTrigger>
          <TabsTrigger
            value="messages"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm px-4 relative"
            data-testid="tab-messages"
          >
            Messages
            {totalUnread > 0 && (
              <span
                className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-rose-600 text-white"
                data-testid="admin-total-unread"
              >
                {totalUnread}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Bills tab */}
        <TabsContent value="bills" className="mt-6">
          <Card className="border-slate-200 shadow-none rounded-md overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-slate-200">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search invoices, clients…"
                  value={searchBill}
                  onChange={(e) => setSearchBill(e.target.value)}
                  data-testid="search-bills-input"
                  className="pl-9 rounded-md border-slate-300"
                />
              </div>
              <Button
                onClick={() => {
                  setPresetClientId(null);
                  setOpenAddBill(true);
                }}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="new-invoice-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> New Invoice
              </Button>
            </div>

            <Table data-testid="bills-table">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="label-tag text-slate-500">Invoice</TableHead>
                  <TableHead className="label-tag text-slate-500">Client</TableHead>
                  <TableHead className="label-tag text-slate-500">Date</TableHead>
                  <TableHead className="label-tag text-slate-500 text-right">
                    Amount
                  </TableHead>
                  <TableHead className="label-tag text-slate-500">Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-400 py-10">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredBills.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-400 py-10">
                      No invoices yet. Click "New Invoice" to create one.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredBills.length > 0 && filteredBills.map((b) => {
                    const firmObj =
                      (businessInfo?.firms || []).find(
                        (f) => f.key === (b.firm || businessInfo?.default_firm)
                      );
                    return (
                    <TableRow key={b.id} data-testid={`bill-row-${b.id}`}>
                      <TableCell className="font-mono font-medium">{b.invoice_no}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{b.client_name}</div>
                        <div className="text-xs text-slate-500">{b.client_email}</div>
                        {firmObj && (businessInfo?.firms?.length || 0) > 1 && (
                          <div
                            className="mt-1 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 rounded-sm"
                            data-testid={`bill-firm-badge-${b.id}`}
                          >
                            {firmObj.name}
                          </div>
                        )}
                        {b.created_by_name && (
                          <div
                            className="mt-1 text-[10px] text-slate-500"
                            data-testid={`bill-created-by-${b.id}`}
                          >
                            Created by {b.created_by_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">{formatDate(b.created_at)}</TableCell>
                      <TableCell className="font-mono text-right text-slate-900">
                        {formatINR(b.total)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={b.status} data-testid={`bill-status-${b.id}`} />
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`bill-actions-${b.id}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={() => setEditingBill(b)}
                              data-testid={`edit-bill-${b.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-2" /> Edit Invoice
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleToggleStatus(b)}
                              data-testid={`toggle-status-${b.id}`}
                            >
                              Mark as {b.status === "paid" ? "Unpaid" : "Paid"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => generateInvoicePDF(b, businessInfo)}
                              data-testid={`download-pdf-${b.id}`}
                            >
                              <Download className="w-4 h-4 mr-2" /> Download PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                sendInvoiceViaWhatsApp(
                                  b,
                                  clients.find((c) => c.id === b.client_id),
                                  businessInfo
                                )
                              }
                              data-testid={`send-whatsapp-${b.id}`}
                            >
                              <MessageCircle className="w-4 h-4 mr-2" /> Send via WhatsApp
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                sendInvoiceViaEmail(
                                  b,
                                  clients.find((c) => c.id === b.client_id),
                                  businessInfo
                                )
                              }
                              data-testid={`send-email-${b.id}`}
                            >
                              <Mail className="w-4 h-4 mr-2" /> Send via Email
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => copyInvoiceMessage(b, businessInfo)}
                              data-testid={`copy-invoice-text-${b.id}`}
                            >
                              <Copy className="w-4 h-4 mr-2" /> Copy invoice text
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-rose-600 focus:text-rose-700"
                              onClick={() => handleDeleteBill(b)}
                              data-testid={`delete-bill-${b.id}`}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Clients tab */}
        <TabsContent value="clients" className="mt-6">
          <Card className="border-slate-200 shadow-none rounded-md overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-slate-200">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search clients…"
                  value={searchClient}
                  onChange={(e) => setSearchClient(e.target.value)}
                  data-testid="search-clients-input"
                  className="pl-9 rounded-md border-slate-300"
                />
              </div>
              <Button
                onClick={() => setOpenAddClient(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="add-client-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Client
              </Button>
            </div>

            <Table data-testid="clients-table">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="label-tag text-slate-500">Client ID</TableHead>
                  <TableHead className="label-tag text-slate-500">Client</TableHead>
                  <TableHead className="label-tag text-slate-500">Contact</TableHead>
                  <TableHead className="label-tag text-slate-500">Linked</TableHead>
                  <TableHead className="label-tag text-slate-500 text-right">
                    Billed
                  </TableHead>
                  <TableHead className="label-tag text-slate-500 text-right">
                    Outstanding
                  </TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-400 py-10">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-400 py-10">
                      No clients yet. Add one to get started.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredClients.length > 0 && filteredClients.map((c) => (
                    <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                      <TableCell className="font-mono font-medium text-slate-900" data-testid={`client-code-${c.id}`}>
                        {c.client_code}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {c.bills_count} invoice{c.bills_count === 1 ? "" : "s"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-700">{c.email || "—"}</div>
                        <div className="text-xs text-slate-500">{c.phone || "—"}</div>
                        {(c.contact_person || c.compuoffice_id) && (
                          <div className="text-[11px] text-slate-400 mt-1 flex flex-wrap gap-x-2">
                            {c.contact_person && <span>Contact: {c.contact_person}</span>}
                            {c.compuoffice_id && (
                              <span className="font-mono">CO: {c.compuoffice_id}</span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.linked_user_email ? (
                          <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm">
                            Linked
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-slate-50 text-slate-600 border border-slate-200 rounded-sm">
                            Pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-right text-slate-900">
                        {formatINR(c.total_billed || 0)}
                      </TableCell>
                      <TableCell
                        className={`font-mono text-right ${
                          (c.unpaid_amount || 0) > 0 ? "text-rose-700" : "text-slate-400"
                        }`}
                      >
                        {formatINR(c.unpaid_amount || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`client-actions-${c.id}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={() => setEditingClient(c)}
                              data-testid={`edit-client-${c.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-2" /> Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setPresetClientId(c.id);
                                setOpenAddBill(true);
                              }}
                              data-testid={`create-bill-for-${c.id}`}
                            >
                              <Plus className="w-4 h-4 mr-2" /> New Invoice
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-rose-600 focus:text-rose-700"
                              onClick={() => handleDeleteClient(c)}
                              data-testid={`delete-client-${c.id}`}
                            >
                              Delete Client
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Messages tab */}
        <TabsContent value="messages" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 h-[70vh] min-h-[500px]">
            {/* Client list */}
            <Card className="border-slate-200 shadow-none rounded-md overflow-hidden flex flex-col" data-testid="messages-client-list">
              <div className="p-3 border-b border-slate-200 space-y-2">
                <p className="label-tag">Conversations</p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    placeholder="Search by name or Client ID…"
                    value={searchThread}
                    onChange={(e) => setSearchThread(e.target.value)}
                    data-testid="search-threads-input"
                    className="pl-8 h-8 text-sm rounded-md border-slate-300"
                  />
                </div>
              </div>
              <div className="overflow-y-auto flex-1">
                {(() => {
                  const q = searchThread.trim().toLowerCase();
                  const filtered = q
                    ? clients.filter(
                        (c) =>
                          (c.name || "").toLowerCase().includes(q) ||
                          (c.client_code || "").toLowerCase().includes(q)
                      )
                    : clients;
                  if (filtered.length === 0) {
                    return (
                      <p className="text-sm text-slate-400 p-4 text-center">
                        {q ? "No matches." : "No clients yet."}
                      </p>
                    );
                  }
                  return filtered.map((c) => {
                    const unread = unreadByClient[c.id] || 0;
                    const active = chatClientId === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setChatClientId(c.id)}
                        className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                          active ? "bg-slate-100" : ""
                        }`}
                        data-testid={`message-thread-${c.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm text-slate-900 truncate">
                            {c.name}
                          </span>
                          {unread > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-rose-600 text-white shrink-0">
                              {unread}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                          {c.client_code}
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            </Card>

            {/* Chat window */}
            {chatClientId ? (
              <ChatWindow
                key={chatClientId}
                clientId={chatClientId}
                currentRole="admin"
                className="h-full"
              />
            ) : (
              <div className="border border-slate-200 bg-white rounded-md flex items-center justify-center text-slate-400 text-sm">
                <div className="text-center">
                  <MessageSquare className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                  Select a client to start chatting
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <AddClientDialog
        open={openAddClient}
        onOpenChange={setOpenAddClient}
        onCreated={loadAll}
      />
      <AddBillDialog
        open={openAddBill}
        onOpenChange={setOpenAddBill}
        clients={clients}
        presetClientId={presetClientId}
        onCreated={loadAll}
      />
      <EditBillDialog
        open={!!editingBill}
        onOpenChange={(o) => !o && setEditingBill(null)}
        bill={editingBill}
        onUpdated={loadAll}
      />
      <EditClientDialog
        open={!!editingClient}
        onOpenChange={(o) => !o && setEditingClient(null)}
        client={editingClient}
        onUpdated={loadAll}
      />
    </AppShell>
  );
}
